import type { MembershipsReport } from "@vaulted/shared";
import { formatCents } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { errorMessage, get } from "../api";
import { ExportButtons } from "../components/ExportButtons";
import { type ReportTable, dollars } from "../components/exportReport";
import { Alert, Loading } from "../components/ui";
import { Stat, Table } from "./Reports";

const fmtDate = (d: string) => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${d.slice(0, 10)}T12:00:00Z`));
const VIA: Record<string, string> = { desk: "Front desk", online: "Online", link: "Link" };

function tables(r: MembershipsReport): ReportTable[] {
  return [
    { title: "By plan", columns: ["Plan", "Kind", "Active", "On hold", "Cancelling", "Monthly"], rows: r.byPlan.map((p) => [p.name, p.kind, p.active, p.onHold, p.cancelling, dollars(p.monthlyCents)]) },
    { title: "On hold", columns: ["Client", "Plan", "Unpaid since", "Amount", "Why"], rows: r.onHold.map((h) => [h.client, h.plan, h.since, dollars(h.amountCents), h.reason ?? ""]) },
    { title: "Cancelling", columns: ["Client", "Plan", "Ends"], rows: r.cancelling.map((c) => [c.client, c.plan, c.endsOn]) },
    { title: "Vouchers outstanding", columns: ["Plan", "Covers", "Unused", "Value"], rows: r.vouchers.map((v) => [v.plan, v.label, v.count, dollars(v.valueCents)]) },
    { title: "Sold, last 30 days", columns: ["Date", "Client", "Plan", "How", "Amount"], rows: r.recentSales.map((s) => [s.date.slice(0, 10), s.client, s.plan, s.via ? (VIA[s.via] ?? "") : "", dollars(s.amountCents)]) },
  ];
}

/** Reports → Memberships: active members, monthly recurring, holds, cancellations, unused vouchers. */
export function MembershipsReportView() {
  const report = useQuery({ queryKey: ["report-memberships"], queryFn: () => get<MembershipsReport>("/api/reports/memberships") });
  if (report.isLoading) return <Loading />;
  if (report.error || !report.data) return <Alert>{errorMessage(report.error)}</Alert>;
  const r = report.data;
  const members = r.byPlan.filter((p) => p.kind === "membership").reduce((s, p) => s + p.active, 0);
  const unused = r.vouchers.reduce((s, v) => s + v.count, 0);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">As of {fmtDate(r.today)}.</p>
        <ExportButtons name={`Memberships ${r.today}`} tables={() => tables(r)} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Active members" value={String(members)} />
        <Stat label="Monthly recurring" value={formatCents(r.recurringCents)} sub="Active memberships" />
        <Stat label="On hold" value={String(r.onHold.length)} sub={r.onHold.length ? "Payment failed" : undefined} />
        <Stat label="Prepaid visits unused" value={String(unused)} sub={formatCents(r.vouchers.reduce((s, v) => s + v.valueCents, 0))} />
      </div>
      <Table
        title="By plan"
        columns={["Plan", "Active", "On hold", "Cancelling", "Monthly"]}
        right={4}
        empty="No memberships or packages yet."
        rows={r.byPlan.map((p) => [p.name, String(p.active), String(p.onHold), String(p.cancelling), p.kind === "membership" ? formatCents(p.monthlyCents) : "—"])}
      />
      <Table title="On hold (payment failed)" columns={["Client", "Plan", "Unpaid since", "Why", "Amount"]} empty="None." rows={r.onHold.map((h) => [h.client, h.plan, fmtDate(h.since), h.reason ?? "", formatCents(h.amountCents)])} />
      <Table title="Cancelling" columns={["Client", "Plan", "Ends"]} empty="None." rows={r.cancelling.map((c) => [c.client, c.plan, fmtDate(c.endsOn)])} />
      <Table title="Vouchers outstanding" columns={["Plan", "Covers", "Unused", "Value"]} right={2} empty="None." rows={r.vouchers.map((v) => [v.plan, v.label, String(v.count), formatCents(v.valueCents)])} />
      <Table title="Sold in the last 30 days" columns={["Date", "Client", "Plan", "How", "Amount"]} empty="None." rows={r.recentSales.map((s) => [fmtDate(s.date), s.client, s.plan, s.via ? VIA[s.via]! : "", formatCents(s.amountCents)])} />
    </div>
  );
}
